import {
  type QuizQuestionChoice,
  type QuizQuestionChoiceOption,
} from '@efdigital/ll-addon-data';
import { DataTypeQuiz } from '@efdigital/ll-addon-data/data-types';
import { generateRandomSlug } from '@efdigital/ll-addon-data/lib/random-slug';
import {
  addClass,
  hasClass,
  removeClass,
} from '@efdigital/ll-addon-ui/utils/css-class-helpers';
import { type OptionWithValue } from '@efdigital/ll-admin-v2/utils/quiz-choices';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { Type } from '@warp-drive/core/types/symbols';

import { type QuizQuestionEditorComponentSignature } from '../../types/question-manager.ts';
import { DEFAULT_RESPONSE } from '../../types/quiz-responses.ts';
import { EditOptions } from './shared/edit-options.gts';
import RequiredCheckbox from './shared/required-checkbox.gts';

type QuizQuestionChoiceFormat = 'radio' | 'horizontal' | 'dropdown';

function eq(a: unknown, b: unknown) {
  return a === b;
}

export class SingleChoiceQuestionEditorComponent extends Component<QuizQuestionEditorComponentSignature> {
  get questionData(): QuizQuestionChoice {
    return this.args.question.choice!;
  }

  updateOptions = (options: QuizQuestionChoiceOption[]) => {
    this.questionData.choices = options;
  };

  addOption = (option?: OptionWithValue) => {
    this.questionData.choices.push({
      [Type]: DataTypeQuiz.QuizQuestionChoiceOption,
      oid: generateRandomSlug(),
      value: option?.value ?? '',
      score: option?.score ?? 0,
      category: '',
      additionalCategories: [],
      correct: false,
      response: DEFAULT_RESPONSE,
    });
  };

  updateOption = (option: QuizQuestionChoiceOption, event: Event) => {
    option.value = (event.target as HTMLInputElement).value;
  };

  removeOptionAt = (index: number) => {
    this.questionData.choices.splice(index, 1);
  };

  setPresets = (options: OptionWithValue[]) => {
    for (const option of options) {
      this.addOption(option);
    }

    const hasAnyScores = options.some((option) => option.score);
    if (hasAnyScores) {
      this.questionData.scoring = 'individual';
    }
  };

  setDisplayFormat = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const format = target.value as QuizQuestionChoiceFormat;

    if (format === 'dropdown') {
      this.questionData.format = 'dropdown';
      this.args.question.css = removeClass(this.args.question.css, 'scale');
      return;
    }

    this.questionData.format = 'radio';

    if (format === 'horizontal') {
      this.args.question.css = addClass(this.args.question.css, 'scale');
    } else {
      this.args.question.css = removeClass(this.args.question.css, 'scale');
    }
  };

  get displayFormat(): QuizQuestionChoiceFormat {
    if (this.questionData.format === 'dropdown') {
      return 'dropdown';
    }
    if (hasClass(this.args.question.css, 'scale')) {
      return 'horizontal';
    }
    return 'radio';
  }

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">
        <RequiredCheckbox @question={{@question}} />

        <div class="mb-3">
          <div class="form-label">Display</div>
          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="radioDefault"
              id="{{uniqueId}}-format-radio"
              value="radio"
              checked={{eq this.displayFormat "radio"}}
              {{on "change" this.setDisplayFormat}}
            />
            <label class="form-check-label" for="{{uniqueId}}-format-radio">
              Radio buttons
            </label>
          </div>

          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="radioDefault"
              id="{{uniqueId}}-format-horizontal"
              value="horizontal"
              checked={{eq this.displayFormat "horizontal"}}
              {{on "change" this.setDisplayFormat}}
            />
            <label
              class="form-check-label"
              for="{{uniqueId}}-format-horizontal"
            >
              Horizontal buttons
            </label>
          </div>

          <div class="form-check">
            <input
              class="form-check-input"
              type="radio"
              name="radioDefault"
              id="{{uniqueId}}-format-dropdown"
              value="dropdown"
              checked={{eq this.displayFormat "dropdown"}}
              {{on "change" this.setDisplayFormat}}
            />
            <label class="form-check-label" for="{{uniqueId}}-format-dropdown">
              Dropdown menu
            </label>
          </div>
        </div>

        <EditOptions
          @uniqueId={{uniqueId}}
          @options={{this.questionData.choices}}
          @updateOptions={{this.updateOptions}}
          @updateOption={{this.updateOption}}
          @removeOptionAt={{this.removeOptionAt}}
          @addOption={{this.addOption}}
          @setPresets={{this.setPresets}}
          @hasScore={{true}}
        />
      </div>
    {{/let}}
  </template>
}
